import React, { useState, useRef } from 'react';
import { Platform } from 'react-native';
import PropTypes from 'prop-types';
import { Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { APP } from '../../../config';

import analytics from '../../../modules/analytics';
import nonCriticalException from '../../../modules/exceptionLogger';
import { getAndroidVersion } from '../../../modules/AppInfo';
import { useJanitor } from '../../../hooks/useJanitor';
import { TextInput } from '../TextInput';
import Button from '../Button';
import { BasicCheckMarkIcon } from '../Icons';

const DefaultCopied = () => (
  <React.Fragment>
    Copied
    <BasicCheckMarkIcon color={BrandColor.Green} width={16} height={16} />
  </React.Fragment>
);

/**
 * Extends the `Button` component with `copyText` property, which on click puts
 * it into the clipboard.
 */
const CopyButton = ({
  copyText,
  text,
  children,
  Copied,
  onCopied,
  showInput = false,
  ...props
}) => {
  const label = text || children;

  const janitor = useJanitor();
  const [showCopied, setShowCopied] = useState(false);
  const textAreaRef = useRef(null); // Only used as part of the fallback approach to copy

  /**
   * Puts the copy text into the clipboard
   *
   * This will attempt to find a useable method for copying text into the clipboard. May
   * throw an exception if it can't find one.
   */
  const copy = async () => {
    // Clipboard API is only available in secure context e.g. HTTPS (not locally)

    // for some reason this doesnt work in android
    const isAndroid = APP && getAndroidVersion();

    if (navigator?.clipboard && !isAndroid) {
      await navigator.clipboard.writeText(copyText);
    } else {
      // Hacky fallback approach to performing a copy. Should only be used in local dev
      textAreaRef.current.focus();
      document.execCommand('copy');
    }
  };

  const handleCopyClicked = async () => {
    analytics.track('copy_button_clicked', { label, copyText });
    try {
      await copy();
    } catch (err) {
      nonCriticalException(`Failed to perform clipboard copy: ${err}`);
      return;
    }

    setShowCopied(true);
    janitor.setTimeout(() => setShowCopied(false), 2000); // Reset showCopied to false after 2s

    if (onCopied) {
      onCopied();
    }
  };

  if (
    Platform.OS !== 'web'
    || !copyText
    || !document?.queryCommandSupported?.('copy')
  ) {
    return null;
  }

  return (
    <Inline>
      <TextInput
        ref={textAreaRef}
        selectTextOnFocus
        editable={false}
        value={copyText}
        style={{
          flex: 1,
          ...(!showInput && { position: 'absolute', left: '-9999px' }),
        }}
      />
      <Button {...props} onClick={handleCopyClicked}>
        {showCopied ? <Copied /> : label}
      </Button>
    </Inline>
  );
};

CopyButton.propTypes = {
  copyText: PropTypes.string.isRequired,
  text: PropTypes.string,
  showInput: PropTypes.bool,
  children: PropTypes.any,
  props: PropTypes.object,
  Copied: PropTypes.any,
  onCopied: PropTypes.func,
};

CopyButton.defaultProps = {
  text: null,
  showInput: false,
  children: null,
  props: null,
  Copied: DefaultCopied,
  onCopied: null,
};

export default CopyButton;
